import dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { DataModel, SerializationContext, SerializedData, DataModelBuilder } from '../dto/data-model.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { TicketTemplate } from './ticket-template';

export class TicketJob extends DataModel {
  static readonly TYPE_ID: string = '4e2d6162ca574f16848d064d4449faba';

  public readonly batchCount: number;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly batchSize: number,
    public readonly ticketCount: number,
    public readonly template: TicketTemplate,
    public readonly name: string,
  ) {
    super();

    this.batchCount = Math.ceil(ticketCount / batchSize);
  }

  get $typeId(): string {
    return TicketJob.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketJob {
    return new TicketJob(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'eventId'),
        MapUtils.get<number>(map, 'batchSize'),
        MapUtils.get<number>(map, 'ticketCount'),
        context.fromData<TicketTemplate>(MapUtils.get<SerializedData>(map, 'template')),
        MapUtils.get<string>(map, 'name'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'batchSize': this.batchSize,
      'ticketCount': this.ticketCount,
      'template': context.toData(this.template),
      'name': this.name,
    };
  }

  clone(): this {
    return new TicketJob(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.eventId,
        this.batchSize,
        this.ticketCount,
        cloneData(this.template),
        this.name,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.creationDate === this.creationDate &&
        isSameData(other.businessRef, this.businessRef) &&
        other.eventId === this.eventId &&
        other.batchSize === this.batchSize &&
        other.ticketCount === this.ticketCount &&
        isSameData(other.template, this.template) &&
        other.name === this.name;
  }
}

export class TicketJobBuilder extends DataModelBuilder<TicketJob> {
  build(context: SerializationContext, map: SerializedData): TicketJob {
    return TicketJob.fromMap(context, map);
  }
}
